<script lang="ts" setup>
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTracking } from '@/utilities/tracking.ts';

const props = defineProps<{
  projectId: number;
  projectName: string;
  startingPrice?: string | null;
  phoneNumber?: string | null;
  formAnchorId: string;
}>();

const emit = defineEmits<{
  contact: [];
}>();

const i18n = useI18n();
const { t } = i18n;
const { trackAction } = useTracking();

// La barre s'efface dès que le formulaire arrive à l'écran : elle n'a plus rien à apporter
// et masquerait le bouton d'envoi pendant la saisie.
const formVisible = ref(false);
let observer: IntersectionObserver | null = null;

const formattedPhoneNumber = computed(() => props.phoneNumber?.formatPhoneNumber(i18n.locale.value));

onMounted(() => {
  const target = document.getElementById(props.formAnchorId);
  if (!target) return;

  observer = new IntersectionObserver((entries) => {
    const entry = entries[0];
    if (entry) {
      formVisible.value = entry.isIntersecting;
    }
  });

  observer.observe(target);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

function onCall() {
  void trackAction('project', props.projectId, 'phoneClick');
  if (props.phoneNumber) {
    window.location.href = `tel:${props.phoneNumber}`;
  }
}
</script>

<template>
  <div
    :class="[
      formVisible ? 'pointer-events-none translate-y-full opacity-0' : 'translate-y-0 opacity-100',
      'no-print fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 pt-3 backdrop-blur transition-all duration-200',
      'pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
    ]"
  >
    <!-- La marge de droite laisse le badge de consentement, ancré dans ce coin, se poser sur du vide. -->
    <div class="mx-auto flex max-w-7xl items-center gap-2 pr-14 lg:gap-4">
      <!-- Le rappel du projet n'apparaît qu'à partir de lg : sous cette largeur, la place entière
           revient aux deux boutons, et le titre de la page reste de toute façon accessible. -->
      <div class="hidden min-w-0 flex-1 lg:block">
        <p class="truncate font-semibold text-gray-900">{{ projectName }}</p>
        <p v-if="startingPrice" class="truncate text-sm text-gray-600">{{ t('views.project.action-bar.starting-at', { price: startingPrice }) }}</p>
      </div>

      <button class="hover:bg-ph-green-600 bg-ph-green-700 flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-white lg:flex-none" type="button" @click="emit('contact')">
        <FontAwesomeIcon :icon="faEnvelope" class="text-ph-green-200 shrink-0" />
        <span>{{ t('views.project.action-bar.contact') }}</span>
      </button>

      <a
        v-if="phoneNumber && phoneNumber.length > 0"
        :aria-label="t('views.project.action-bar.call')"
        :href="`tel:${phoneNumber}`"
        class="border-ph-green-700 text-ph-green-700 hover:bg-ph-green-50 flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold"
        @click.prevent="onCall"
      >
        <FontAwesomeIcon :icon="faPhone" class="shrink-0" />
        <!-- Le numéro écrit remplace le libellé dès qu'il y a la place : il se note et se compose
             ailleurs, ce qu'un bouton « Appeler » ne permet pas. -->
        <span class="xs:inline hidden md:hidden">{{ t('views.project.action-bar.call') }}</span>
        <span class="hidden md:inline">{{ formattedPhoneNumber }}</span>
      </a>
    </div>
  </div>
</template>

<style lang="css" scoped></style>
